import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Collapsible from '@dashboard/components/Collapsible';
import ReminderEmailForm from './ReminderEmailForm';
import { usePreview } from '@dashboard/context/PreviewContext';
import usePage from '@dashboard/hooks/usePage';
import { Form } from '@dashboard/components/Formik';
import Callout from '@/dashboard/components/Callout';
import Link from '@/dashboard/components/Link';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';

const CheckoutAbandonmentReminderEmailsForm = () => {
  const { seller } = useAuth();
  const { page, updatePage } = usePage();
  const [openReminder, setOpenReminder] = useState(null);
  const [, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  const handleToggle = reminderId => {
    setOpenReminder(openReminder === reminderId ? null : reminderId);
  };

  useEffect(() => {
    if (openReminder === null) {
      setPreview({
        type: 'reminderEmailMessage',
        reminderNumber: 1,
      });
    }
  }, [openReminder]);

  useEffect(() => {
    return () => {
      setPreview({
        type: 'checkout',
      });
    };
  }, []);

  const reminders = [
    { id: 1, title: '1st reminder email', subtitle: 'Sent after 1 hour' },
    { id: 2, title: '2nd reminder email', subtitle: 'Sent after 24 hours' },
    { id: 3, title: '3rd reminder email', subtitle: 'Sent after 48 hours' },
  ];

  const initialValues = {
    showStoreLogo: page?.checkoutAbandonment?.showStoreLogo !== false,
    showStoreName: page?.checkoutAbandonment?.showStoreName !== false,
  };

  const sellerEmailsDisabled = seller?.checkoutAbandonment?.sendEmails !== true;
  const pageEmailsDisabled = page?.checkoutAbandonment?.disableEmails === true;

  const callout = sellerEmailsDisabled
    ? {
        link: '/settings/cart-abandonment',
        text: 'store settings',
      }
    : pageEmailsDisabled
      ? {
          link: `/pages/${page._id}/customize/settings/abandonment`,
          text: 'page settings',
        }
      : null;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {callout && (
          <Callout variant="info">
            <Callout.Heading>Email reminders disabled</Callout.Heading>
            <Callout.Content>
              Turn on reminder emails to recover lost sales in your{' '}
              <Link href={callout.link}>{callout.text}</Link>
            </Callout.Content>
          </Callout>
        )}

        {reminders.map(reminder => {
          const isOpen = openReminder === reminder.id;

          return (
            <div key={reminder.id}>
              <Collapsible.Root open={isOpen} onOpenChange={() => handleToggle(reminder.id)}>
                <Collapsible.Trigger
                  className={clsx(
                    'py-3 px-3 bg-white border border-gray-200 w-full flex gap-x-3 justify-between rounded transition-colors',
                    {
                      'hover:border-gray-300': !isOpen,
                      '!border-chocolate-1000': isOpen,
                    },
                  )}
                >
                  <div className="flex flex-col items-start justify-start gap-y-0.5">
                    <div className="text-base font-semibold break-words">{reminder.title}</div>
                    <div className="text-sm text-gray-700">{reminder.subtitle}</div>
                  </div>

                  <div className="flex items-center">
                    {isOpen ? (
                      <ChevronUpIcon className="w-4 h-4" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4" />
                    )}
                  </div>
                </Collapsible.Trigger>

                <Collapsible.Content className="my-4">
                  <ReminderEmailForm reminderNumber={reminder.id} />
                </Collapsible.Content>
              </Collapsible.Root>
            </div>
          );
        })}
      </div>

      <Form
        autosave
        showState={false}
        initialValues={initialValues}
        onSubmit={async values => {
          const payload = {
            checkoutAbandonment: {
              ...page?.checkoutAbandonment,
              showStoreLogo: values.showStoreLogo,
              showStoreName: values.showStoreName,
            },
          };

          const { data, error } = await updatePage.mutateAsync(payload);

          return { data, error };
        }}
        fields={() => {
          return [
            {
              type: 'label',
              name: 'label',
              label: 'Show on emails',
              noMargin: true,
            },
            {
              type: 'checkbox',
              name: 'showStoreLogo',
              label: 'Logo',
              noMargin: true,
            },
            {
              type: 'checkbox',
              name: 'showStoreName',
              label: 'Store name',
              noMargin: true,
            },
          ];
        }}
      ></Form>
    </div>
  );
};

export default CheckoutAbandonmentReminderEmailsForm;
